import {
  CAPABILITY_KEYS,
  GROW_PACKAGE_ID,
  MANAGE_PACKAGE_ID,
  SIMPLY_LISTINGS_PACKAGE_ID,
  TOOL_CODES,
  TRACK_PACKAGE_ID
} from '@brightlocal/shared';

import { CORE_COUNTRY_CODES, REVIEWS_COUNTRY_CODES } from 'shared/modules/country-support';

import type { ToolGate } from './types';

/** Sentry feature tag for errors and warnings raised inside this module. */
export const CAPABILITIES_FEATURE_TAG = 'capabilities';

/** The query key for the customer capabilities API. */
export const CUSTOMER_CAPABILITIES_QUERY_KEY = ['customer-capabilities'] as const;

/** The query key for the location capabilities API. */
export const LOCATION_CAPABILITIES_QUERY_KEY = 'location-capabilities';

/** Screen shown in place of a tool the customer can't use, picked from the unavailability reasons. */
export const UNAVAILABLE_SCREEN = {
  NO_ACTIVE_PLAN: 'no-active-plan',
  UNSUPPORTED_COUNTRY: 'unsupported-country',
  NOT_IN_PLAN: 'not-in-plan',
  GENERAL: 'general'
} as const;

/** Union of the screens shown in place of an unavailable tool. */
export type UnavailableScreen = (typeof UNAVAILABLE_SCREEN)[keyof typeof UNAVAILABLE_SCREEN];

/** GTM `eventAction` of the tool-unavailable screen events: which variant of the screen showed. */
export const TOOL_UNAVAILABLE_ACTION = {
  NO_ACTIVE_PLAN: 'no_active_plan',
  NOT_IN_PLAN: 'not_in_plan',
  PLAN_CHANGE_VIA_SUPPORT: 'plan_change_via_support',
  GENERAL: 'general'
} as const;

/** Union of the tool-unavailable screen variants as GTM reports them. */
export type ToolUnavailableAction = (typeof TOOL_UNAVAILABLE_ACTION)[keyof typeof TOOL_UNAVAILABLE_ACTION];

/** The plan screens' variant for any plan that isn't a new plan: the plan is changed through support, not on the plans page. */
export const PLAN_CHANGE_VIA_SUPPORT = 'plan-change-via-support';

/** What is shown for an unavailable tool: its screen, or the plan screens' support variant. */
export type UnavailableVariant = UnavailableScreen | typeof PLAN_CHANGE_VIA_SUPPORT;

/**
 * Package ids of the new plans (Track, Grow, Manage, Simply Listings), which the customer changes on
 * the plans page. Every other package (a Custom plan, CB-only, an older plan) is sent to support
 * instead, so a plan the customer can't change is never sent to the plans page.
 */
export const NEW_PLAN_PACKAGE_IDS = [
  TRACK_PACKAGE_ID,
  GROW_PACKAGE_ID,
  MANAGE_PACKAGE_ID,
  SIMPLY_LISTINGS_PACKAGE_ID
] as const;

/**
 * Gates of the tool pages, one per page, and of the AI Insights sections inside Location Summary;
 * `ToolGate` documents each field.
 * TODO: give AI Visibility a location tool code [https://brightlocal.atlassian.net/browse/FP-359]
 * TODO: use the backend country answer instead of the country lists [https://brightlocal.atlassian.net/browse/FP-161]
 * TODO: decide what Simply Listings packages get on Location Summary [https://brightlocal.atlassian.net/browse/FP-559]
 */
export const TOOL_GATES = {
  // The Location Summary page gate already blocks Simply Listings and unsupported countries.
  AI_INSIGHTS: {
    toolCode: TOOL_CODES.AI_INSIGHTS
  },
  AI_VISIBILITY: {
    customerKey: CAPABILITY_KEYS.AI_VISIBILITY,
    supportedCountries: CORE_COUNTRY_CODES
  },
  LOCATION_SUMMARY: {
    isUnavailableForSimplyListings: true,
    supportedCountries: CORE_COUNTRY_CODES
  },
  LOCAL_SEARCH_GRID: {
    toolCode: TOOL_CODES.LSG,
    supportedCountries: CORE_COUNTRY_CODES
  },
  LOCAL_RANK_TRACKER: {
    toolCode: TOOL_CODES.LRT,
    supportedCountries: CORE_COUNTRY_CODES
  },
  REVIEWS: {
    toolCode: TOOL_CODES.RM,
    supportedCountries: REVIEWS_COUNTRY_CODES
  }
} satisfies Record<string, ToolGate>;
